import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useWindowManager } from "../../lib/windowManager/WindowManagerContext";
import { getVisibleSections } from "../../lib/windowManager/registry";
import StartMenu from "./StartMenu";
import { Sun, Moon } from "lucide-react";

export default function Taskbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { windows, openWindow, minimizeWindow } = useWindowManager();
  const [menuOpen, setMenuOpen] = useState(false);
  const [now, setNow] = useState(null);
  const barRef = useRef(null);

  useEffect(() => {
    const tick = () => setNow(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  // Close the Start Menu on any outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDown(e) {
      if (barRef.current && !barRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const sections = getVisibleSections(isAdmin);
  const running = sections.filter((s) => windows[s.id] && windows[s.id].status !== "closed");
  const topZ = Object.values(windows).reduce((max, w) => Math.max(max, w.zIndex), 0);

  function onTaskbarClick(entry) {
    const w = windows[entry.id];
    if (entry.route !== location.pathname) {
      navigate(entry.route);
      return;
    }
    if (!w) return;
    if (w.status === "minimized") openWindow(entry.id);
    else if (w.status === "open") minimizeWindow(entry.id);
  }

  return (
    <div className="win98-taskbar win-raised" ref={barRef}>
      <button
        type="button"
        className={`win-btn win98-start-btn${menuOpen ? " win98-start-btn--active" : ""}`}
        onClick={() => setMenuOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={menuOpen}
      >
        🏁 Start
      </button>
      <div className="win98-taskbar-running">
        {running.map((entry) => {
          const w = windows[entry.id];
          const active = w.status === "open" && w.zIndex === topZ;
          return (
            <button
              key={entry.id}
              type="button"
              className={`win-btn win98-taskbar-item${active ? " win98-taskbar-item--active" : ""}`}
              onClick={() => onTaskbarClick(entry)}
            >
              <span aria-hidden="true">{entry.icon}</span> {entry.title}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="win-btn win-btn-icon win98-tray-toggle"
        onClick={toggleTheme}
        aria-label={theme === "dark" ? "Switch to Light Win98" : "Switch to Dark Win98"}
        title={theme === "dark" ? "Light Win98" : "Dark Win98"}
      >
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
      <div className="win98-tray win-well">
        <span>{now || "--:--"}</span>
      </div>
      {menuOpen && <StartMenu onClose={() => setMenuOpen(false)} />}
    </div>
  );
}
